import { c as _c } from "react/compiler-runtime";
import { Box, Separator, Heading, HStack, List, VStack, Text } from '@chakra-ui/react';
import type { BoxProps } from '@chakra-ui/react';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';

/** Shared styles for mission list highlight matches — edit here only. */
const MISSION_HIGHLIGHT_STYLES: BoxProps = {
  display: 'inline',
  px: '1',
  py: '1',
  borderRadius: 'xs',
  bg: 'colorPalette.bg',
  color: 'colorPalette.fg',
  fontWeight: 600,
  css: {
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone'
  }
};
const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
type MissionTextHighlightProps = {
  queries: string[];
  children: string;
};

/** Inline highlight spans that wrap naturally across line breaks (unlike Chakra Highlight). */
const MissionTextHighlight: React.FC<MissionTextHighlightProps> = t0 => {
  const $ = _c(20);
  const {
    queries,
    children
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== children || $[1] !== queries) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const text = children.replace(/\s+/g, " ").trim();
      if (queries.length === 0) {
        t5 = <>{text}</>;
        break bb0;
      }
      let t6;
      if ($[8] !== queries) {
        t6 = new Set(queries.map(_temp));
        $[8] = queries;
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      const queryLookup = t6;
      let t7;
      if ($[10] !== queries) {
        t7 = [...queries].sort(_temp2).map(escapeRegExp).join("|");
        $[10] = queries;
        $[11] = t7;
      } else {
        t7 = $[11];
      }
      const pattern = t7;
      const parts = text.split(new RegExp(`(${pattern})`, "i"));
      T0 = Text;
      t1 = "span";
      t2 = "inline-block";
      t3 = "100%";
      let t8;
      if ($[12] !== queryLookup) {
        t8 = (part, index) => {
          if (!part) {
            return null;
          }
          if (queryLookup.has(part.toLowerCase())) {
            return <Box key={index} as="span" {...MISSION_HIGHLIGHT_STYLES}>{part}</Box>;
          }
          return part;
        };
        $[12] = queryLookup;
        $[13] = t8;
      } else {
        t8 = $[13];
      }
      t4 = parts.map(t8);
    }
    $[0] = children;
    $[1] = queries;
    $[2] = T0;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    T0 = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[14] !== T0 || $[15] !== t1 || $[16] !== t2 || $[17] !== t3 || $[18] !== t4) {
    t6 = <T0 as={t1} display={t2} width={t3}>{t4}</T0>;
    $[14] = T0;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
export const MissionBlock: React.FC = () => {
  const $ = _c(37);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const isMobile = getBreakpointValue([true, true, false, false, false]);
  let t0;
  if ($[0] !== getBreakpointValue) {
    t0 = getBreakpointValue([10]);
    $[0] = getBreakpointValue;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = isMobile ? "2rem" : "10rem";
  let t2;
  if ($[2] !== getBreakpointValue) {
    t2 = getBreakpointValue([4, 4, 10, 10, 10]);
    $[2] = getBreakpointValue;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Separator flex="1" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== getBreakpointValue) {
    t4 = getBreakpointValue(["xl", "2xl", "3xl"]);
    $[5] = getBreakpointValue;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <Text color="teal.fg" flexShrink="0" fontSize={t4} fontWeight={700} letterSpacing="wide" textAlign="center" textStyle="heading" textTransform="uppercase">Mission</Text>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Separator flex="1" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <HStack align="center" gap={10} justify="center" width="full">{t3}{t5}{t6}</HStack>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== getBreakpointValue) {
    t8 = getBreakpointValue(["3xl", "4xl", "5xl", "6xl"]);
    $[12] = getBreakpointValue;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <Heading color="orange.fg" size={t8} textAlign="center">What if anyone could launch a startup?</Heading>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = isMobile ? "lg" : "2xl";
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <List.Item fontStyle="italic">(Really... anyone).</List.Item>;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <List.Item><MissionTextHighlight queries={["willingness to go do something incredible?"]}>What if all it took to launch a startup was an idea and the willingness to go do something incredible?</MissionTextHighlight></List.Item>;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <List.Item><MissionTextHighlight queries={["go away?"]}>What if all of the dumb stuff that makes 90% of startups fail could just... go away?</MissionTextHighlight></List.Item>;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <List.Item><MissionTextHighlight queries={["do exactly what they're passionate about?"]}>What would the world look like with hundreds of millions of Founders who get to do exactly what they're passionate about?</MissionTextHighlight></List.Item>;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <List.Item><MissionTextHighlight queries={["wildly innovative products"]}>How many wildly innovative products would there be?</MissionTextHighlight></List.Item>;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <List.Item><MissionTextHighlight queries={["empowered", "feel"]}>How much more empowered would people feel?</MissionTextHighlight></List.Item>;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <List.Item><MissionTextHighlight queries={["only limited by their imagination?"]}>What happens when everyone is finally only limited by their imagination?</MissionTextHighlight></List.Item>;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <List.Item><MissionTextHighlight queries={["not"]}>{"For us, that's not a \"What if?\""}</MissionTextHighlight></List.Item>;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <List.Item><MissionTextHighlight queries={["breaking down all of the barriers"]}>We're in the business of breaking down all of the barriers that prevent startups from launching and causing them to fail.</MissionTextHighlight></List.Item>;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <List.Item><MissionTextHighlight queries={["lonely", "frustrated", "under-resourced"]}>We're in the business of no longer leaving Founders lonely, frustrated, and under-resourced.</MissionTextHighlight></List.Item>;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  let t21;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <List.Item><MissionTextHighlight queries={["business of launching startups"]}>We are in the business of launching startups.</MissionTextHighlight></List.Item>;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <List.Item><MissionTextHighlight queries={["This is what we do."]}>We're Startups.com. This is what we do.</MissionTextHighlight></List.Item>;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  let t23;
  if ($[28] !== t10) {
    t23 = <List.Root as="ul" color="red.fg" fontSize={t10} fontWeight={500} gap={8} maxWidth="35rem" opacity={1} variant="plain" width="full">{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}</List.Root>;
    $[28] = t10;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] !== t0 || $[31] !== t1 || $[32] !== t2 || $[33] !== t23 || $[34] !== t7 || $[35] !== t9) {
    t24 = <VStack align="center" borderRadius="3xl" gap={t0} height="full" justify="stretch" maxWidth="container.md" overflow="hidden" paddingBottom={t1} paddingX={t2} width="full">{t7}{t9}{t23}</VStack>;
    $[30] = t0;
    $[31] = t1;
    $[32] = t2;
    $[33] = t23;
    $[34] = t7;
    $[35] = t9;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  return t24;
};
function _temp(query) {
  return query.toLowerCase();
}
function _temp2(a, b) {
  return b.length - a.length;
}